import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Contact from '../components/Contact';
import { LayerTags } from '../components/Projects';
import { projects, categoryLabel } from '../data/portfolio';
import './ProjectsPage.css';

const categoryLabelLong = { web: 'Web Development', mobile: 'Mobile App', freelance: 'Freelance Project' };
const filters = [
  { key: 'all', label: 'All' },
  { key: 'web', label: 'Web' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'freelance', label: 'Freelance' },
];

const projectCategories = (project) => project.categories || [project.category];
const countFor = (key) =>
  key === 'all' ? projects.length : projects.filter((p) => projectCategories(p).includes(key)).length;

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <motion.div
      className="project-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-bar mono">
          <span>{categoryLabelLong[project.category]} · {project.year}</span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close" autoFocus>
            Close ✕
          </button>
        </div>

        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">{project.title}</h2>
          <LayerTags active={project.layers} />
        </div>

        <div className="modal-links mono">
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="btn btn--accent">
              <i className={project.liveIcon || 'fa-solid fa-arrow-up-right-from-square'} aria-hidden="true" />
              {project.liveButton || project.liveLabel || 'Visit live site'}
            </a>
          )}
          {project.storeLinks?.map((link) => (
            <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="btn">
              <i className={link.icon} aria-hidden="true" />
              {link.label}
            </a>
          ))}
        </div>

        {project.images?.length > 0 && (
          <div className={`modal-images ${project.portrait ? 'portrait' : ''}`}>
            {project.images.map((image, index) => (
              <img key={image} src={image} alt={`${project.title} screenshot ${index + 1}`} loading="lazy" />
            ))}
          </div>
        )}

        <div className="modal-body">
          <p className="modal-description">{project.fullDescription}</p>

          <div className="modal-cols">
            {project.features && (
              <div>
                <h3 className="eyebrow modal-subtitle">Key features</h3>
                <ul className="modal-features">
                  {project.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h3 className="eyebrow modal-subtitle">Built with</h3>
              <div className="modal-tags">
                {project.technologies.map((tech) => (
                  <span key={tech} className="tag">{tech}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ProjectsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const closeModal = useCallback(() => setSelectedProject(null), []);

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter((project) => projectCategories(project).includes(activeCategory));

  return (
    <div className="projects-page">
      <SEO
        title="Projects"
        description="View my portfolio: Transactfy POS & Inventory (Flutter app on Google Play), the RideWatch school bus tracking website and apps, and freelance sites like Tessa's BGC Condo and StaySuitePH — built with Flutter, WordPress, and JavaScript."
        url="/projects"
        keywords="Flutter Developer, POS App, Transactfy, RideWatch, WordPress Developer, Freelance Web Developer Philippines, Mobile App Developer, Full Stack Portfolio"
      />
      <Navbar />

      <main className="wrap">
        <section className="archive-head">
          <span className="eyebrow">[02] — Archive</span>
          <h1 className="display archive-title">
            All the{' '}<br /><span className="accent">work.</span>
          </h1>
          <p className="archive-lede">
            Mobile apps and websites I’ve built for clients and for my own products. Open one
            for screenshots, features and store links.
          </p>
        </section>

        <div className="archive-filters" role="group" aria-label="Filter projects">
          {filters.filter((f) => countFor(f.key) > 0).map((f) => (
            <button
              key={f.key}
              type="button"
              className="chip"
              aria-pressed={activeCategory === f.key}
              onClick={() => setActiveCategory(f.key)}
            >
              {f.label} ({countFor(f.key)})
            </button>
          ))}
        </div>

        <section className="archive-grid" aria-label="Projects">
          {filteredProjects.map((project, i) => (
            <motion.article
              key={project.id}
              className="archive-card"
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <button
                type="button"
                className="archive-open"
                onClick={() => setSelectedProject(project)}
                aria-label={`Open ${project.title} details`}
              >
                <span className="archive-image">
                  <img src={project.images[0]} alt="" loading="lazy" />
                  <span className="archive-view mono">View details →</span>
                </span>
              </button>
              <div className="archive-body">
                <div className="archive-meta mono">
                  <span>/{String(i + 1).padStart(2, '0')}</span>
                  <span>{project.year} · {categoryLabel[project.category]}</span>
                </div>
                <h2 className="archive-name">
                  <button type="button" onClick={() => setSelectedProject(project)}>
                    {project.title}
                  </button>
                </h2>
                <p className="archive-desc">{project.description}</p>
                <div className="archive-foot">
                  <LayerTags active={project.layers} />
                  {project.liveUrl && (
                    <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="archive-live mono">
                      {project.liveLabel || project.liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </section>
      </main>

      <Contact />

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={closeModal} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsPage;
